import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiLoader, FiChevronDown, FiChevronUp, FiAlertCircle, FiTag, FiMapPin } from 'react-icons/fi';

// ─── Exam catalogue (10 supported) ───────────────────────────────────────────

const EXAMS = [
  { v: 'neet',      l: 'NEET UG',      sub: 'All India · MBBS / BDS / BAMS',  rankLabel: 'NEET AIR Rank',        featured: true },
  { v: 'neet_pg',   l: 'NEET PG',      sub: 'MD / MS / PG Diploma',           rankLabel: 'NEET PG Rank' },
  { v: 'aiims',     l: 'AIIMS UG',     sub: 'AIIMS Entrance Exam',            rankLabel: 'AIIMS Rank' },
  { v: 'ini_cet',   l: 'INI-CET',      sub: 'AIIMS / PGIMER PG',             rankLabel: 'INI-CET Rank' },
  { v: 'ayush',     l: 'AYUSH',        sub: 'BAMS / BHMS / BUMS',            rankLabel: 'NEET AIR Rank' },
  { v: 'neet_mds',  l: 'NEET MDS',     sub: 'Dental PG Admission',           rankLabel: 'NEET MDS Rank' },
  { v: 'up_neet',   l: 'UP State',     sub: 'Uttar Pradesh Quota',           rankLabel: 'NEET AIR Rank' },
  { v: 'maha_neet', l: 'Maha NEET',    sub: 'Maharashtra State',             rankLabel: 'NEET AIR Rank' },
  { v: 'mht_cet',   l: 'MHT-CET',      sub: 'Maharashtra CET',               rankLabel: 'MHT-CET Percentile' },
  { v: 'raj_neet',  l: 'Raj NEET',     sub: 'Rajasthan State Quota',         rankLabel: 'NEET AIR Rank' },
];

// ─── Static option data ───────────────────────────────────────────────────────

const STATES = [
  { v: 'andaman',          l: 'Andaman & Nicobar' },
  { v: 'andhra_pradesh',   l: 'Andhra Pradesh' },
  { v: 'arunachal_pradesh',l: 'Arunachal Pradesh' },
  { v: 'assam',            l: 'Assam' },
  { v: 'bihar',            l: 'Bihar' },
  { v: 'chandigarh',       l: 'Chandigarh' },
  { v: 'chhattisgarh',     l: 'Chhattisgarh' },
  { v: 'dadra',            l: 'Dadra & Nagar Haveli' },
  { v: 'daman',            l: 'Daman & Diu' },
  { v: 'delhi',            l: 'Delhi' },
  { v: 'goa',              l: 'Goa' },
  { v: 'gujarat',          l: 'Gujarat' },
  { v: 'haryana',          l: 'Haryana' },
  { v: 'himachal_pradesh', l: 'Himachal Pradesh' },
  { v: 'jammu',            l: 'Jammu & Kashmir' },
  { v: 'jharkhand',        l: 'Jharkhand' },
  { v: 'karnataka',        l: 'Karnataka' },
  { v: 'kerala',           l: 'Kerala' },
  { v: 'lakshadweep',      l: 'Lakshadweep' },
  { v: 'madhya_pradesh',   l: 'Madhya Pradesh' },
  { v: 'maharashtra',      l: 'Maharashtra' },
  { v: 'manipur',          l: 'Manipur' },
  { v: 'meghalaya',        l: 'Meghalaya' },
  { v: 'mizoram',          l: 'Mizoram' },
  { v: 'nagaland',         l: 'Nagaland' },
  { v: 'orissa',           l: 'Odisha' },
  { v: 'pondicherry',      l: 'Puducherry' },
  { v: 'punjab',           l: 'Punjab' },
  { v: 'rajasthan',        l: 'Rajasthan' },
  { v: 'sikkim',           l: 'Sikkim' },
  { v: 'tamil_nadu',       l: 'Tamil Nadu' },
  { v: 'telangana',        l: 'Telangana' },
  { v: 'tripura',          l: 'Tripura' },
  { v: 'uttar_pradesh',    l: 'Uttar Pradesh' },
  { v: 'uttarakhand',      l: 'Uttarakhand' },
  { v: 'west_bengal',      l: 'West Bengal' },
];

const GENDERS    = [{ v:'male',l:'Male' },{ v:'female',l:'Female' },{ v:'other',l:'Other' }];
const CATEGORIES = [
  { v:'gen',l:'General (GEN)' },{ v:'obc',l:'OBC' },{ v:'sc',l:'SC' },
  { v:'st',l:'ST' },{ v:'ews',l:'EWS' },{ v:'pwd',l:'PwD' },
];
const FEE_RANGES = [
  { v:'f1',l:'< ₹1L' },{ v:'f2',l:'₹1–2L' },{ v:'f3',l:'₹2–3L' },
  { v:'f4',l:'₹3–5L' },{ v:'f5',l:'> ₹5L' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtFees = (fees) => {
  if (!fees || fees === 'N/A') return '—';
  if (typeof fees === 'number') {
    if (fees >= 100000) return `₹${(fees / 100000).toFixed(1)}L/yr`;
    return `₹${fees.toLocaleString('en-IN')}`;
  }
  return String(fees);
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const Label = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
    {children}
  </label>
);

const Select = ({ value, onChange, children }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all appearance-none cursor-pointer"
  >
    {children}
  </select>
);

const FeeChip = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 ${
      active
        ? 'bg-primary-800 border-primary-800 text-white'
        : 'bg-white border-slate-200 text-gray-600 hover:border-primary-300 hover:text-primary-800'
    }`}
  >
    {label}
  </button>
);

const CollegeCard = ({ college, index }) => {
  const [open, setOpen] = useState(true);
  const courses = college.tuples || [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
    >
      <button
        className="w-full flex items-start justify-between gap-3 p-5 text-left hover:bg-slate-50 transition-colors duration-150"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 font-heading text-base leading-snug line-clamp-2">
            {college.heading || 'Unknown College'}
          </p>
          {college.location && (
            <span className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-gray-500 font-medium">
              <FiMapPin className="text-[10px]" />{college.location}
            </span>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2 mt-0.5">
          <span className="text-xs text-gray-400 font-medium hidden sm:block">
            {courses.length} course{courses.length !== 1 ? 's' : ''}
          </span>
          {open ? <FiChevronUp className="text-gray-400" /> : <FiChevronDown className="text-gray-400" />}
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && courses.length > 0 && (
          <motion.div
            key="courses"
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-100 divide-y divide-slate-100">
              {courses.map((course, ci) => {
                const exam   = course.exams?.[0] ?? {};
                const round  = exam.roundNo ?? '—';
                const cutoff = exam.result   ?? '—';
                return (
                  <div key={ci} className="px-5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                    <span className="flex-1 min-w-[160px] text-gray-700 font-medium">
                      {course.name || 'Unknown Course'}
                    </span>
                    <span className="text-primary-700 font-semibold tabular-nums whitespace-nowrap">
                      {fmtFees(course.fees)}
                    </span>
                    <span className="text-gray-500 whitespace-nowrap text-xs">
                      Round <strong className="text-gray-700">{round}</strong>
                      {cutoff !== '—' && (
                        <> · Rank <strong className="text-gray-700">{cutoff}</strong></>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const NeetPredictor = () => {
  const [form, setForm] = useState({
    exam: 'neet', rank: '', gender: 'male', category: 'gen',
    state: 'uttar_pradesh', fees: [], page: 1,
  });
  const [results,  setResults]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');
  const [hasMore,  setHasMore]  = useState(false);

  const setField = (key) => (val) => setForm(p => ({ ...p, [key]: val, page: 1 }));

  const toggleFee = (fee) =>
    setForm(p => ({
      ...p,
      fees: p.fees.includes(fee) ? p.fees.filter(f => f !== fee) : [...p.fees, fee],
    }));

  const activeExam = EXAMS.find(e => e.v === form.exam) || EXAMS[0];

  const doFetch = useCallback(async (page = 1) => {
    const rank = parseInt(form.rank);
    if (!rank || rank < 1 || rank > 9999999) {
      setError(`Enter a valid ${activeExam.rankLabel}.`);
      return;
    }
    setLoading(true);
    setError('');
    if (page === 1) { setResults(null); setSearch(''); }

    const params = new URLSearchParams({
      exam: form.exam, rank, gender: form.gender,
      category: form.category, state: form.state, page,
    });
    form.fees.forEach(f => params.append('fees[]', f));

    try {
      const res  = await fetch(`/api/neet-predict?${params}`);
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Unknown error');
      setResults(prev => page === 1 ? data.results : [...(prev || []), ...data.results]);
      setHasMore(data.results.length >= 10);
      setForm(p => ({ ...p, page }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [form, activeExam]);

  const handleSubmit = (e) => { e.preventDefault(); doFetch(1); };

  const filtered = (results || []).filter(c =>
    !search || c.heading?.toLowerCase().includes(search.toLowerCase())
  );

  const otherExams = EXAMS.filter(e => !e.featured);

  return (
    <section id="neet-predictor" className="py-20 bg-base-DEFAULT">
      <div className="container mx-auto px-4">

        {/* Section header */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-medium text-primary-800 mb-4">
            <FiTag className="text-xs" />
            Free Tool
          </span>
          <h2 className="text-section font-semibold font-heading tracking-[-0.02em] text-gray-950" style={{ textWrap: 'balance' }}>
            Medical Admission Predictor
          </h2>
          <p className="mt-3 text-lg text-gray-600 max-w-2xl mx-auto" style={{ textWrap: 'pretty' }}>
            NEET, NEET PG, AIIMS, AYUSH, state quotas — enter your rank to instantly see which colleges you may qualify for.
          </p>
        </div>

        {/* Form card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8 mb-8 max-w-5xl mx-auto">

          {/* ── Exam selector ── */}
          <div className="mb-7">
            <Label>Select Exam</Label>

            {/* Featured NEET button */}
            <button
              type="button"
              onClick={() => setField('exam')('neet')}
              className={`w-full mb-3 flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border-2 text-left transition-all duration-150 ${
                form.exam === 'neet'
                  ? 'border-primary-800 bg-primary-50'
                  : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50'
              }`}
            >
              <div>
                <p className={`font-bold font-heading text-base ${form.exam === 'neet' ? 'text-primary-800' : 'text-gray-900'}`}>
                  NEET UG
                  <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider rounded-full bg-primary-800 text-white px-2 py-0.5">
                    Most Popular
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">All India · MBBS / BDS / BAMS</p>
              </div>
              <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                form.exam === 'neet' ? 'border-primary-800' : 'border-slate-300'
              }`}>
                {form.exam === 'neet' && <div className="w-2 h-2 rounded-full bg-primary-800" />}
              </div>
            </button>

            {/* Other 9 exams — 3-column grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {otherExams.map(exam => (
                <button
                  key={exam.v}
                  type="button"
                  onClick={() => setField('exam')(exam.v)}
                  className={`flex items-start gap-2.5 px-3 py-3 rounded-xl border text-left transition-all duration-150 ${
                    form.exam === exam.v
                      ? 'border-primary-800 bg-primary-50'
                      : 'border-slate-200 bg-white hover:border-primary-200 hover:bg-slate-50'
                  }`}
                >
                  <div className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${
                    form.exam === exam.v ? 'border-primary-800' : 'border-slate-300'
                  }`}>
                    {form.exam === exam.v && <div className="w-1.5 h-1.5 rounded-full bg-primary-800" />}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-semibold leading-tight ${form.exam === exam.v ? 'text-primary-800' : 'text-gray-800'}`}>
                      {exam.l}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">{exam.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <h3 className="text-sm font-semibold text-gray-700 font-heading mb-4">Your Details</h3>

            {/* Core inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div>
                <Label>{activeExam.rankLabel} *</Label>
                <input
                  type="number"
                  min={1}
                  max={9999999}
                  placeholder="e.g. 28181"
                  value={form.rank}
                  onChange={e => setField('rank')(e.target.value)}
                  required
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all placeholder-gray-400"
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={form.gender} onChange={setField('gender')}>
                  {GENDERS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onChange={setField('category')}>
                  {CATEGORIES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
                </Select>
              </div>
              <div>
                <Label>Domicile State</Label>
                <Select value={form.state} onChange={setField('state')}>
                  {STATES.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                </Select>
              </div>
            </div>

            {/* Fee range */}
            <div className="mb-6">
              <Label>Annual Fee Range <span className="normal-case font-normal text-gray-400">(optional)</span></Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {FEE_RANGES.map(f => (
                  <FeeChip
                    key={f.v}
                    label={f.l}
                    active={form.fees.includes(f.v)}
                    onClick={() => toggleFee(f.v)}
                  />
                ))}
                {form.fees.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm(p => ({ ...p, fees: [] }))}
                    className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-start gap-2 mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <FiAlertCircle className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-8 py-3 bg-primary-800 text-white rounded-full font-semibold hover:bg-primary-900 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
            >
              {loading
                ? <><FiLoader className="animate-spin" /> Fetching predictions…</>
                : <><FiSearch /> Get Predictions</>}
            </button>
          </form>
        </div>

        {/* Results */}
        <AnimatePresence>
          {results !== null && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-5xl mx-auto"
            >
              {/* Results header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-medium text-gray-600">
                    {filtered.length === 0
                      ? 'No colleges found — try adjusting your filters.'
                      : <>Showing <strong className="text-gray-900">{filtered.length}</strong> college{filtered.length !== 1 ? 's' : ''} for <strong className="text-gray-900">{activeExam.l}</strong></>}
                  </p>
                  <button
                    onClick={() => { setResults(null); setSearch(''); setError(''); }}
                    className="text-xs text-gray-400 hover:text-red-500 underline transition-colors duration-150 shrink-0"
                  >
                    Clear results
                  </button>
                </div>
                <div className="relative max-w-xs w-full">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                  <input
                    type="search"
                    placeholder="Search college name…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all"
                  />
                </div>
              </div>

              {/* College cards */}
              <div className="space-y-3">
                {filtered.length === 0 && !loading && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-gray-500 text-sm">
                    {search
                      ? `No colleges match "${search}".`
                      : 'No results found for this combination. Try adjusting rank, category, or state.'}
                  </div>
                )}
                {filtered.map((college, i) => (
                  <CollegeCard key={`${college.heading}-${i}`} college={college} index={i} />
                ))}
              </div>

              {/* Load more */}
              {hasMore && !search && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => doFetch(form.page + 1)}
                    disabled={loading}
                    className="px-6 py-2.5 rounded-full border border-primary-200 text-primary-800 text-sm font-medium hover:bg-primary-50 hover:border-primary-400 disabled:opacity-60 transition-all duration-200"
                  >
                    {loading ? 'Loading…' : 'Load more colleges'}
                  </button>
                </div>
              )}

              {/* Disclaimer — no mention of data source provider */}
              <p className="mt-6 text-xs text-gray-400 text-center max-w-2xl mx-auto">
                Predictions are based on historical admission cutoff data and are indicative only. Actual admission depends on final counselling rounds. Contact Vaishnavi Educare for personalised guidance.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default NeetPredictor;
